import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import { useCurrentStateAndParams, useRouter } from "@uirouter/react";

import { chosenSites } from "app/common/services/chosenSites";
import { CompareTabs } from "app/components/compareTabs/compareTabs";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useCompareDataQuery } from "./useCompareDataQuery";

export interface Imeta {
    id: string;
    title: string;
    icon: string;
    numOfResults: number;
}

interface IQueryParamsForCompareTabs {
    from: string;
    to: string;
    webSource?: string;
    keys?: string[];
    country?: number;
    duration?: string;
    isWWW?: boolean;
    channel?: number;
    includeSubDomains?: boolean;
    key?: string;
}

export interface ICompareTabs {
    keyName: string;
    onSelectSite: (name, numOfResults) => void;
    domainQueryParamName: string;
    api?: string;
    queryParamsForCompareTabs?: IQueryParamsForCompareTabs;
    hideResults?: boolean;
    requestBody?: any;
}

export const CompareTabsContainer: FunctionComponent<ICompareTabs> = (props) => {
    const { api, keyName, onSelectSite, domainQueryParamName, queryParamsForCompareTabs, hideResults = false } = props;
    const { params } = useCurrentStateAndParams();
    const router = useRouter();

    const domains = chosenSites.sitelistForLegend();
    const domain = params[domainQueryParamName] ? params[domainQueryParamName] : domains[0].name;

    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedSiteDomain, setSelectedSiteDomain] = useState(domain);

    const displayResults = !hideResults && !!api && !!queryParamsForCompareTabs;

    const { data: compareData, isLoading } = useCompareDataQuery({
        api,
        queryParamsForCompareTabs,
        requestBody: undefined,
        enabled: displayResults,
    });

    const isDomainSelected = domains.some((site) => site.name === domain);

    // All programmatic writes of the selected-site param in this container are housekeeping, not
    // user navigations, so they replace the current history entry. A push here stacks a second
    // entry on top of the navigation that triggered it — leaving the page then takes two Back
    // presses (e.g. picking a suggested advertiser, or removing a domain in the asset picker).
    useEffect(() => {
        if (!isDomainSelected) {
            void proNavigator.applyUpdateParams(
                {
                    [domainQueryParamName]: domains[0].name,
                },
                { location: "replace" },
            );
        }
    }, [isDomainSelected]);

    const onTabSelected = (index: number) => {
        onSelectSite(domains[index].name, tabs[index].numOfResults);
    };

    useEffect(() => {
        setSelectedSiteDomain(domain);
    }, [domain]);

    useEffect(() => {
        if (domains) {
            setSelectedTab(domains.findIndex((domain) => domain.name === selectedSiteDomain));
        }
    }, [selectedSiteDomain, domains]);

    const firstLoadCompareTab = useCallback(() => {
        const firstLoad = sessionStorage.getItem(keyName);
        if (!firstLoad && compareData) {
            const result = domains.find((domain) => compareData[domain.name] && compareData[domain.name] !== 0);
            if (result) {
                sessionStorage.setItem(keyName, "true");
                void proNavigator.go(
                    proNavigator.current().name,
                    {
                        ...proNavigator.getParams(),
                        [domainQueryParamName]: result.name,
                    },
                    { location: "replace" },
                );
            }
        }
    }, [keyName, compareData, domains, domainQueryParamName]);

    useEffect(() => {
        return () => {
            const currentTransition = router.globals.transition;
            const isNavigatingToAnotherState =
                currentTransition && currentTransition.from().name !== currentTransition.to().name;

            if (!chosenSites.isCompare() && !isNavigatingToAnotherState) {
                proNavigator.applyUpdateParams(
                    {
                        [domainQueryParamName]: null,
                    },
                    { location: "replace" },
                );
            }
        };
    }, [domainQueryParamName, router]);

    useEffect(() => {
        if (compareData) {
            firstLoadCompareTab();
        }
    }, [compareData, firstLoadCompareTab]);

    const tabs: Imeta[] = domains.map((domain, index) => {
        return {
            id: `${domain.name}_${index}`,
            title: domain.name,
            icon: domain.icon,
            numOfResults: compareData?.[domain.name] ?? 0,
        };
    });

    return (
        <CompareTabs
            tabs={tabs}
            tabSelectedIndex={selectedTab}
            onTabSelectedCallback={onTabSelected}
            isLoading={isLoading}
            hideResults={hideResults}
        />
    );
};
